// Visualisation geometry for the pathway visualiser.
//
// Project-specific presentation layer — turns the (portable) detention periods
// into chart coordinates. The `scale` array defines the vertical ordering of
// MHA statuses in the chart; `generateVisualisationFromPeriods` samples the
// periods into line points + event circles. None of this belongs in the core
// detention-period logic.
import type { DetentionPeriod, MhaStatusRow } from "@thalamos/pathway-logic";

export const scale = [
  ["Not Detained", 0],
  ["Expired", 0],
  ["Section 136", 1],
  ["Section 5(4)", 2],
  ["Section 5(2)", 3],
  ["Section 4", 4],
  ["Section 2", 5],
  ["Section 3", 6],
  ["Section 17A (Recalled)", 7],
  ["Section 17A", 8],
] as const satisfies [string, number][];

/**
 * Generate visualisation data from cleaned detention periods.
 * Creates line segments based on actual detention periods and circles for significant events.
 */
export function generateVisualisationFromPeriods(
  periods: DetentionPeriod[],
  entries: MhaStatusRow[],
  daysArray: Date[],
  patientYOffset: number,
): {
  line: { x: number; y: number }[];
  circles: { x: number; y: number; event: MhaStatusRow }[];
} {
  const line: { x: number; y: number }[] = [];
  const circles: { x: number; y: number; event: MhaStatusRow }[] = [];

  // Build line from periods - shows the actual MHA status over time
  for (const day of daysArray) {
    const activePeriod = periods.find(
      (p) => day >= p.startDate && day <= p.endDate,
    );

    if (activePeriod) {
      const y = scale.find((s) => s[0] === activePeriod.section)?.[1] ?? 0;
      line.push({ x: day.getTime(), y: y + patientYOffset });
    } else if (periods.some((p) => p.endDate < day)) {
      // After any period has ended, show Not Detained (0). We only need to know
      // one exists, not which — so `.some` avoids a filter + sort of every
      // period on each day with no active detention.
      line.push({ x: day.getTime(), y: 0 + patientYOffset });
    }
  }

  // Create circles for key events only:
  // - First event of each period (section start)
  // - Last event of each period (section end/transition)
  // - Hospital/trust changes within a period
  const significantEventIds = new Set<string>();

  for (const period of periods) {
    const firstEvent = period.events[0];
    const lastEvent = period.events.at(-1);
    if (firstEvent !== undefined && lastEvent !== undefined) {
      significantEventIds.add(firstEvent.id); // Section start
      significantEventIds.add(lastEvent.id); // Section end

      // Track hospital/trust changes within the period
      let lastHospital = firstEvent.hospital_name;
      let lastTrust = firstEvent.trust_name;
      for (const event of period.events) {
        if (
          (event.hospital_name && event.hospital_name !== lastHospital) ||
          (event.trust_name && event.trust_name !== lastTrust)
        ) {
          significantEventIds.add(event.id);
          lastHospital = event.hospital_name;
          lastTrust = event.trust_name;
        }
      }
    }
  }

  // Also add Not Detained / Expired events
  for (const entry of entries) {
    if (entry.mha_status === "Not Detained" || entry.mha_status === "Expired") {
      significantEventIds.add(entry.id);
    }
  }

  // Generate circles only for significant events
  for (const entry of entries) {
    if (significantEventIds.has(entry.id)) {
      const y = scale.find((s) => s[0] === entry.mha_status)?.[1] ?? 0;
      circles.push({
        // Plot each event at when it actually happened (effective_date_time) —
        // NOT `created` (a bulk-load timestamp that detaches the dot from its
        // line) and NOT mha_status_start_date_time (which a continuation event,
        // e.g. a same-section trust transfer, shares with the original start, so
        // the two dots would collide on the section's start date).
        x: new Date(entry.effective_date_time).getTime(),
        y: y + patientYOffset,
        event: entry,
      });
    }
  }

  circles.sort((a, b) => a.x - b.x);
  return { line, circles };
}
